import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, CarFront, Wrench, User } from "lucide-react";
import { useLang } from "@/i18n";
import { submitRequest } from "@/config";
import { Field, Input, Textarea, Select, Gdpr, Honeypot, PhotoUpload, SuccessBox, useFormState } from "@/components/FormBits";
import { toast } from "sonner";

export default function Offerte() {
  const { t } = useLang();
  const [step, setStep] = useState(0);
  const [files, setFiles] = useState([]);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, set, setData] = useFormState({
    brand: "", model: "", year: "", plate: "", vin: "", km: "", fuel: "", transmission: "",
    service: "", problem: "", name: "", phone: "", email: "", preferred: "WhatsApp", website: "", gdpr: false,
  });

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    if (!data.service) setData((d) => ({ ...d, service: t("quote.services")[0], fuel: d.fuel || t("form.fuels")[0], transmission: d.transmission || t("form.transmissions")[0] }));
  }, []);

  const stepValid = () => {
    if (step === 0) return data.brand && data.model && data.year && data.plate;
    if (step === 1) return data.service && data.problem;
    return data.name && data.phone && data.email && data.gdpr;
  };

  const submit = async () => {
    setLoading(true);
    try { await submitRequest("quote", data, files); setDone(true); window.scrollTo(0, 0); }
    catch (err) { toast.error(err.message); }
    setLoading(false);
  };

  const steps = [t("quote.s1"), t("quote.s2"), t("quote.s3")];
  const stepIcons = [CarFront, Wrench, User];

  return (
    <div className="container-x py-14 max-w-3xl" data-testid="offerte-page">
      <p className="overline-red">Milano Worx</p>
      <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white">{t("quote.title")}</h1>
      <p className="mt-3 text-zinc-400">{t("quote.sub")}</p>

      {done ? (
        <div className="mt-10"><SuccessBox message={t("quote.success")} testid="quote-success" /></div>
      ) : (
        <div className="mt-10" data-testid="quote-wizard-container">
          <div className="flex items-center gap-2 mb-8">
            {steps.map((s, i) => {
              const Icon = stepIcons[i];
              return (
                <div key={i} className="flex items-center gap-2 flex-1" data-testid={`quote-step-indicator-${i}`}>
                  <div className={`flex items-center gap-2 ${i <= step ? "text-white" : "text-zinc-600"}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${i <= step ? "bg-[#E11D48] border-[#E11D48]" : "border-zinc-700"}`}>
                      <Icon size={15} className={i <= step ? "text-white" : "text-zinc-600"} />
                    </div>
                    <span className="hidden sm:block text-xs font-semibold uppercase tracking-wider">{s}</span>
                  </div>
                  {i < 2 && <div className={`flex-1 h-px ${i < step ? "bg-[#E11D48]" : "bg-zinc-800"}`} />}
                </div>
              );
            })}
          </div>

          <div className="card-dark p-6 grid gap-4">
            <Honeypot value={data.website} onChange={set("website")} />

            {step === 0 && (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="quote-step-1">
                <Field label={t("form.brand")} required><Input value={data.brand} onChange={set("brand")} data-testid="q-brand" /></Field>
                <Field label={t("form.model")} required><Input value={data.model} onChange={set("model")} data-testid="q-model" /></Field>
                <Field label={t("form.year")} required><Input type="number" min="1980" max="2030" value={data.year} onChange={set("year")} data-testid="q-year" /></Field>
                <Field label={t("form.plate")} required><Input value={data.plate} onChange={set("plate")} data-testid="q-plate" /></Field>
                <Field label={t("form.vin")}><Input value={data.vin} onChange={set("vin")} data-testid="q-vin" /></Field>
                <Field label={t("form.km")}><Input type="number" value={data.km} onChange={set("km")} data-testid="q-km" /></Field>
                <Field label={t("form.fuel")}><Select options={t("form.fuels")} value={data.fuel} onChange={set("fuel")} data-testid="q-fuel" /></Field>
                <Field label={t("form.transmission")}><Select options={t("form.transmissions")} value={data.transmission} onChange={set("transmission")} data-testid="q-transmission" /></Field>
              </div>
            )}

            {step === 1 && (
              <div className="grid gap-4" data-testid="quote-step-2">
                <Field label={t("quote.choose")} required>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {t("quote.services").map((s) => (
                      <button type="button" key={s} onClick={() => setData((d) => ({ ...d, service: s }))} data-testid={`q-service-${s.toLowerCase().replace(/\s|\/+/g, "-")}`}
                        className={`py-2.5 px-2 rounded-lg border text-xs font-semibold transition-colors ${data.service === s ? "border-[#E11D48] bg-[#E11D48]/10 text-white" : "border-zinc-700 text-zinc-400 hover:text-white"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label={t("form.problem")} required><Textarea rows={4} value={data.problem} onChange={set("problem")} data-testid="q-problem" /></Field>
                <Field label={t("form.photos")}><PhotoUpload files={files} setFiles={setFiles} testid="q-photos" /></Field>
              </div>
            )}

            {step === 2 && (
              <div className="grid gap-4" data-testid="quote-step-3">
                <Field label={t("form.name")} required><Input value={data.name} onChange={set("name")} data-testid="q-name" /></Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label={t("form.phone")} required><Input type="tel" value={data.phone} onChange={set("phone")} data-testid="q-phone" /></Field>
                  <Field label={t("form.email")} required><Input type="email" value={data.email} onChange={set("email")} data-testid="q-email" /></Field>
                </div>
                <Field label={t("form.preferred")}>
                  <div className="flex gap-2">
                    {t("form.prefs").map((p) => (
                      <button type="button" key={p} onClick={() => setData((d) => ({ ...d, preferred: p }))} data-testid={`q-pref-${p.toLowerCase()}`}
                        className={`flex-1 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${data.preferred === p ? "border-[#E11D48] bg-[#E11D48]/10 text-white" : "border-zinc-700 text-zinc-400"}`}>
                        {p}
                      </button>
                    ))}
                  </div>
                </Field>
                <Gdpr checked={data.gdpr} onChange={(v) => setData((d) => ({ ...d, gdpr: v }))} />
              </div>
            )}

            <div className="flex gap-3 mt-2">
              {step > 0 && (
                <button onClick={() => setStep(step - 1)} data-testid="quote-back-btn" className="btn-outline">
                  <ChevronLeft size={17} />{t("quote.back")}
                </button>
              )}
              {step < 2 ? (
                <button onClick={() => stepValid() && setStep(step + 1)} disabled={!stepValid()} data-testid="quote-next-btn"
                  className="btn-red flex-1 disabled:opacity-40 disabled:cursor-not-allowed">
                  {t("quote.next")}<ChevronRight size={17} />
                </button>
              ) : (
                <button onClick={submit} disabled={!stepValid() || loading} data-testid="quote-submit-btn"
                  className="btn-red flex-1 disabled:opacity-40 disabled:cursor-not-allowed">
                  {loading ? "..." : t("quote.send")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
